const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_RETRY_DELAYS_MS = [750, 1500];
// Per-attempt watchdog. Kept low because a stalled (non-erroring) load blocks
// the signup user for roughly (timeout * attempts) + retry delays before the
// failure screen is shown.
const DEFAULT_TIMEOUT_MS = 5000;
const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js";
const DEFAULT_CALLBACK_NAME = "onloadTurnstileCallback";

/**
 * @typedef {object} TurnstileLoadFailure
 * @property {number} attempt
 * @property {number} maxAttempts
 * @property {string} failureType
 * @property {string} scriptSrc
 * @property {boolean} hasTurnstile
 * @property {unknown} error
 */

/**
 * @typedef {object} LoadTurnstileOptions
 * @property {Window & Record<string, any> & {turnstile?: unknown}} win
 * @property {Document} document
 * @property {(metadata: {attempt: number, maxAttempts: number, scriptSrc: string}) => void} renderTurnstile
 * @property {(failure: TurnstileLoadFailure) => void} onFinalFailure
 * @property {number} [maxAttempts]
 * @property {number[]} [retryDelaysMs]
 * @property {number} [timeoutMs]
 * @property {string} [callbackName]
 * @property {(handler: TimerHandler, timeout?: number, ...arguments: any[]) => number} [setTimeoutFn]
 * @property {(id?: number) => void} [clearTimeoutFn]
 */

/**
 * Load Cloudflare Turnstile with bounded retries and a watchdog timeout.
 *
 * Turnstile's `onload` callback is global, so each attempt gets a unique
 * callback name. This keeps late callbacks from previous attempts from
 * rendering duplicate widgets after a retry has already started or succeeded.
 *
 * @param {LoadTurnstileOptions} options
 */
export function loadTurnstileWithRetry(options) {
  const maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const retryDelaysMs = options.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const callbackName = options.callbackName ?? DEFAULT_CALLBACK_NAME;
  const setTimeoutFn =
    options.setTimeoutFn ?? options.win.setTimeout.bind(options.win);
  const clearTimeoutFn =
    options.clearTimeoutFn ?? options.win.clearTimeout.bind(options.win);
  /** @type {number | undefined} */
  let activeAttempt;
  let isFinished = false;
  /** @type {(number | undefined)[]} */
  const timeoutIds = [];

  /**
   * @param {number} attempt
   */
  const getCallbackName = (attempt) => `${callbackName}Attempt${attempt}`;

  /**
   * @param {number} attempt
   */
  const clearAttemptTimeout = (attempt) => {
    const timeoutId = timeoutIds[attempt];
    if (timeoutId != null) clearTimeoutFn(timeoutId);
    timeoutIds[attempt] = undefined;
  };

  /**
   * @param {number} attempt
   */
  const ignoreAttemptCallback = (attempt) => {
    options.win[getCallbackName(attempt)] = () => {};
  };

  /**
   * @param {number} attempt
   * @param {string} failureType
   * @param {string} scriptSrc
   * @param {unknown} error
   */
  const failOrRetry = (attempt, failureType, scriptSrc, error) => {
    if (isFinished || activeAttempt !== attempt) return;
    clearAttemptTimeout(attempt);
    ignoreAttemptCallback(attempt);

    if (attempt < maxAttempts) {
      const delay = retryDelaysMs[attempt - 1] ?? 0;
      setTimeoutFn(() => startAttempt(attempt + 1), delay);
      return;
    }

    isFinished = true;
    options.onFinalFailure({
      attempt,
      maxAttempts,
      failureType,
      scriptSrc,
      hasTurnstile: options.win["turnstile"] != null,
      error,
    });
  };

  /**
   * @param {number} attempt
   */
  function startAttempt(attempt) {
    if (isFinished) return;
    activeAttempt = attempt;

    const attemptCallbackName = getCallbackName(attempt);
    const script = options.document.createElement("script");
    const scriptSrc = createTurnstileScriptSrc(attemptCallbackName, attempt);
    script.async = true;
    script.src = scriptSrc;

    options.win[attemptCallbackName] = () => {
      if (isFinished || activeAttempt !== attempt) return;
      clearAttemptTimeout(attempt);
      try {
        options.renderTurnstile({ attempt, maxAttempts, scriptSrc });
        isFinished = true;
      } catch (error) {
        failOrRetry(attempt, "render-error", scriptSrc, error);
      }
    };

    script.onerror = (error) => {
      failOrRetry(attempt, "script-error", scriptSrc, error);
    };

    timeoutIds[attempt] = setTimeoutFn(() => {
      failOrRetry(
        attempt,
        "timeout",
        scriptSrc,
        new Error(`Turnstile did not load within ${timeoutMs}ms`),
      );
    }, timeoutMs);

    options.document.head.appendChild(script);
  }

  startAttempt(1);
}

/**
 * @param {string} callbackName
 * @param {number} attempt
 */
export function createTurnstileScriptSrc(callbackName, attempt) {
  const url = new URL(TURNSTILE_SCRIPT_URL);
  url.searchParams.set("onload", callbackName);
  if (attempt > 1) url.searchParams.set("retryAttempt", String(attempt));
  return url.toString();
}
